<template>
  <div class="cinema-detail-page">
    <loading v-if="loading"></loading>
    <div v-else class="page-content">
      <!-- 顶部影院信息卡片 - 固定不滚动 -->
      <div class="cinema-header-card">
        <div class="cinema-name">{{ movieName }}</div>
        <div class="cinema-info-row">
          <van-icon name="location-o" size="0.9rem" color="#999" />
          <span class="cinema-addr">{{ movieAddress }}</span>
          <span v-if="cinemaDistance" class="cinema-distance">{{ cinemaDistance }}</span>
        </div>
      </div>

      <!-- 电影海报轮播区域 - 固定不滚动，紧凑布局 -->
      <div class="movie-carousel-section">
        <carousel-3d 
          :perspective="10" 
          :display="5" 
          :width="100" 
          :height="140" 
          :space="130" 
          :controlsHeight="70"
          :startIndex="startIndex" 
          @before-slide-change="onBeforeSlideChange">
          <slide v-for="(slide, index) in headData" :index="index" :key="slide.filmId || index">
            <div class="movie-slide-item" :class="{ active: index === headIndex }">
              <img class="slide-poster" :src="slide.pic" />
              <div class="poster-score" v-if="slide.grade">{{ slide.grade }}分</div>
              <div class="poster-version" v-if="slide.versionTypes">{{ slide.versionTypes }}</div>
            </div>
          </slide>
        </carousel-3d>
        
        <!-- 当前电影信息 - 紧凑排列 -->
        <div class="current-movie-info">
          <span class="current-movie-name">{{ headData[headIndex]?.name }}</span>
          <span class="movie-meta">{{ headData[headIndex]?.duration }}分钟 / {{ headData[headIndex]?.filmTypes }}</span>
          <span class="detail-link" @click="goMovieDetail(headData[headIndex])">详情</span>
        </div>
      </div>

      <!-- 日期Tab -->
      <div class="date-tabs-container" v-if="!showsLoading && data.length > 0">
        <div class="date-tabs">
          <div 
            v-for="(item, index) in data" 
            :key="index"
            class="date-tab"
            :class="{ active: navIndex === index }"
            @click="scrollTo(index)">
            {{ getTime(item.showTime) }}
          </div>
        </div>
      </div>

      <!-- 场次列表 -->
      <div class="showtime-scroll-area">
        <div v-if="showsLoading" class="shows-skeleton">
          <div class="skeleton-card" v-for="n in 4" :key="n">
            <van-skeleton :row="2" />
          </div>
        </div>

        <template v-else-if="currentShowList.length > 0">
          <div 
            v-for="(item1, index1) in currentShowList" 
            :key="index1" 
            class="showtime-card"
            @click="goDetail(item1, data[navIndex])">
            <div class="time-section">
              <div class="show-time">{{ item1.showTime }}</div>
              <div class="end-time">{{ item1.endTime }}散场</div>
            </div>
            
            <div class="hall-section">
              <div class="version-type">{{ item1.showVersionType }}</div>
              <div class="hall-name">{{ item1.hallName }}</div>
            </div>
            
            <div class="price-section">
              <div class="price-row">
                <span class="price-value">{{ $utils.getPricePoint(item1.netPrice) }}</span>
                <span class="price-unit">点</span>
                <span class="price-suffix">起</span>
              </div>
              <div class="buy-btn">购票</div>
            </div>
          </div>
          <div class="finished-text">没有更多了</div>
        </template>

        <!-- 空状态 -->
        <div v-else class="empty-state">
          <div class="empty-icon">🎬</div>
          <div class="empty-text">该日期暂无场次</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Carousel3d, Slide } from 'vue-carousel-3d'

// 停稳后再请求，快速连滑 A→B→C 只请求 C
const SHOWS_FETCH_DEBOUNCE_MS = 450

export default {
  name: 'CinemaDetail',
  components: { Carousel3d, Slide },
  data() {
    return {
      loading: true,
      showsLoading: false,
      navIndex: 0,
      headIndex: 0,
      startIndex: 0,
      movieName: '',
      movieAddress: '',
      cinemaDistance: '',
      form: {
        cardId: '',
        cinemaId: '',
        filmId: '',
        cityCode: '',
        ticketType: '',
        current: 1,
        size: 500
      },
      headData: [],
      data: [],
      time: null,
      showsRequestSeq: 0,
      pendingFilmIndex: null,
      loadShowsTimer: null,
    };
  },
  computed: {
    currentShowList() {
      if (this.data.length > 0 && this.data[this.navIndex]) {
        return this.data[this.navIndex].futureMovieDetailList || [];
      }
      return [];
    }
  },
  methods: {
    // 将当前选中影片写入路由，返回时可凭 filmId 重新定位并拉最新场次
    syncFilmIdToRoute(filmId) {
      if (!filmId || String(this.$route.query.filmId) === String(filmId)) {
        return;
      }
      this.$router.replace({
        path: this.$route.path,
        query: {
          ...this.$route.query,
          filmId,
        },
      }).catch(() => {});
    },

    goMovieDetail(val) {
      this.$router.push({
        path: "/MovieDetail",
        query: {
          showbutton: 2,
          item: JSON.stringify(val),
        },
      });
    },
    
    goDetail(val, val1) {
      if (this.showsLoading) {
        return;
      }
      const currentFilm = this.headData[this.headIndex];
      if (!currentFilm || !val || !val.showId || !val1) {
        return;
      }
      const ticketQuery = {
        id: val.showId,
        movieName: currentFilm.name,
        cinemaId: this.form.cinemaId,
        filmId: currentFilm.filmId,
        movieAddress: this.movieAddress,
        cinema_name: this.movieName,
        showDate: val1.showTime,
        startTime: val.showTime,
        endTime: val.endTime,
        hallName: val.hallName,
        cardId: this.form.cardId,
        showVersionType: val.showVersionType,
        moviePicUrl: currentFilm.pic,
      };
      // 进选座前把 filmId 写入历史记录，浏览器返回时能定位到当前影片
      this.syncFilmIdToRoute(currentFilm.filmId);
      this.$router.push({
        path: "/Ticket",
        query: ticketQuery,
      });
    },
    
    scrollTo(index) {
      this.navIndex = index;
    },
    
    getTime(str) {
      const date = this.$moment(str);
      const today = this.$moment();
      const tomorrow = this.$moment().add(1, 'day');
      
      if (date.isSame(today, 'day')) {
        return '今天';
      } else if (date.isSame(tomorrow, 'day')) {
        return '明天';
      } else {
        return date.format("M月D日");
      }
    },
    
    // 轮播一开始切换就清空场次（before-slide-change 在动画开始前触发）
    // 防抖后再请求，避免连滑 A→B→C 打三次接口
    onBeforeSlideChange(index) {
      const i = typeof index === 'object' ? index.index : index;
      if (i == null) {
        return;
      }
      const film = this.headData[i];
      if (!film) {
        return;
      }
      // 同一影片且已有场次，不重复处理
      if (i === this.headIndex && !this.showsLoading && this.data.length > 0) {
        return;
      }

      this.headIndex = i;
      this.form.filmId = film.filmId;
      this.syncFilmIdToRoute(film.filmId);
      this.data = [];
      this.navIndex = 0;
      this.showsLoading = true;
      this.pendingFilmIndex = i;

      clearTimeout(this.loadShowsTimer);
      this.loadShowsTimer = setTimeout(() => {
        this.fetchShows(this.pendingFilmIndex);
      }, SHOWS_FETCH_DEBOUNCE_MS);
    },

    // 首次进入：立即请求，不防抖
    loadShowsForFilm(index) {
      const film = this.headData[index];
      if (!film) {
        return;
      }
      this.headIndex = index;
      this.form.filmId = film.filmId;
      this.data = [];
      this.navIndex = 0;
      this.showsLoading = true;
      this.pendingFilmIndex = index;
      this.fetchShows(index);
    },

    // 请求场次；showsRequestSeq 丢弃过期响应（B 晚于 C 返回时忽略 B）
    fetchShows(index) {
      const film = this.headData[index];
      if (!film || index !== this.headIndex) {
        return;
      }

      const seq = ++this.showsRequestSeq;
      this.form.filmId = film.filmId;

      this.$api
        .queryShowsByMovieAndFilm(this.form)
        .then((res) => {
          if (seq !== this.showsRequestSeq) {
            return;
          }
          if (res.code == 1) {
            this.$Toast(res.msg);
            this.data = [];
          } else {
            this.data = res.data || [];
            this.navIndex = 0;
            if (this.time && this.data.length > 0) {
              this.data.forEach((element, idx) => {
                if (this.time == element.showTime) {
                  this.navIndex = idx;
                }
              });
            }
          }
          this.showsLoading = false;
        })
        .catch(() => {
          if (seq !== this.showsRequestSeq) {
            return;
          }
          this.data = [];
          this.showsLoading = false;
        });
    },
    
    queryAllFilms() {
      const form = {
        cardId: this.form.cardId,
        cinemaId: this.form.cinemaId,
        cityCode: this.form.cityCode,
        ticketType: this.form.ticketType,
      };
      this.$api
        .queryAllFilms(form)
        .then((res) => {
          this.loading = false;
          if (res.code == 1) {
            this.$Toast(res.msg);
            return;
          }
          this.headData = res.data || [];
          if (this.form.filmId && this.headData.length > 0) {
            this.headData.forEach((element, index) => {
              if (element.filmId == this.form.filmId) {
                this.startIndex = index;
                this.headIndex = index;
              }
            });
          } else if (this.headData.length > 0) {
            this.form.filmId = this.headData[0].filmId;
          }
          if (this.headData.length > 0) {
            this.loadShowsForFilm(this.headIndex);
          }
        })
        .catch((err) => {
          this.$Toast(err);
          this.loading = false;
        });
    },
  },

  beforeDestroy() {
    clearTimeout(this.loadShowsTimer);
  },

  mounted() {
    this.form.cardId = localStorage.getItem("cardId");
    this.form.cinemaId = this.$route.query.cinemaId;
    this.form.filmId = this.$route.query.filmId;
    this.movieName = this.$route.query.name;
    this.time = this.$route.query.time;
    this.movieAddress = this.$route.query.address;
    this.cinemaDistance = this.$route.query.distance || '';
    this.form.ticketType = localStorage.getItem("entrCode");
    this.form.cityCode = localStorage.getItem("cityCode");
    this.queryAllFilms();
  },
};
</script>

<style lang='scss' scoped>
.cinema-detail-page {
  height: 100vh;
  background: #f5f5f5;
  overflow: hidden;
}

.page-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 影院信息卡片 */
.cinema-header-card {
  flex-shrink: 0;
  background: #fff;
  padding: 0.8rem 1rem;
}

.cinema-name {
  font-size: 1.05rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.4rem;
}

.cinema-info-row {
  display: flex;
  align-items: flex-start;
  gap: 0.3rem;
}

.cinema-addr {
  flex: 1;
  font-size: 0.8rem;
  color: #999;
  line-height: 1.4;
}

.cinema-distance {
  flex-shrink: 0;
  font-size: 0.85rem;
  color: #ee0a24;
  font-weight: 500;
}

/* 电影海报区域 - 紧凑布局 */
.movie-carousel-section {
  flex-shrink: 0;
  background: #f5f5f5;
  padding: 0.5rem 0;
}

.movie-slide-item {
  width: 100%;
  height: 100%;
  border-radius: 0.4rem;
  overflow: hidden;
  position: relative;
  background: #000;
  
  &.active {
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  }
}

.slide-poster {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.poster-score {
  position: absolute;
  top: 0;
  left: 0;
  background: linear-gradient(135deg, #ff6b4a 0%, #ff8a65 100%);
  padding: 0.08rem 0.3rem;
  border-bottom-right-radius: 0.4rem;
  color: #fff;
  font-size: 0.55rem;
  font-weight: 600;
  z-index: 2;
}

.poster-version {
  position: absolute;
  bottom: 0.2rem;
  right: 0.2rem;
  background: rgba(0, 0, 0, 0.6);
  padding: 0.05rem 0.25rem;
  border-radius: 0.15rem;
  font-size: 0.45rem;
  color: #fff;
  z-index: 2;
}

/* 当前电影信息 - 单行紧凑 */
.current-movie-info {
  text-align: center;
  padding: 0.4rem 1rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.current-movie-name {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
}

.movie-meta {
  font-size: 0.75rem;
  color: #999;
}

.detail-link {
  color: #ee0a24;
  font-size: 0.75rem;
  font-weight: 500;
  border-bottom: 1px solid #ee0a24;
  cursor: pointer;
}

/* 日期Tab */
.date-tabs-container {
  flex-shrink: 0;
  background: #fff;
  padding: 0.6rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.date-tabs {
  display: flex;
  overflow-x: auto;
  padding: 0 0.8rem;
  gap: 0.5rem;
  -webkit-overflow-scrolling: touch;
  
  &::-webkit-scrollbar {
    display: none;
  }
}

.date-tab {
  flex-shrink: 0;
  padding: 0.4rem 0.9rem;
  font-size: 0.8rem;
  color: #666;
  background: #f5f5f5;
  border-radius: 1rem;
  white-space: nowrap;
  transition: all 0.2s ease;
  
  &.active {
    background: linear-gradient(135deg, #ee0a24 0%, #ff6b4a 100%);
    color: #fff;
    font-weight: 500;
  }
}

/* 场次滚动区域 - 独立滚动 */
.showtime-scroll-area {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: #fff;
  padding: 0.8rem;
}

/* 骨架屏 */
.shows-skeleton {
  .skeleton-card {
    background: #f9f9f9;
    border-radius: 0.5rem;
    padding: 1rem;
    margin-bottom: 0.6rem;
  }
}

/* 场次卡片 */
.showtime-card {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 0.6rem;
  padding: 0.8rem;
  margin-bottom: 0.5rem;
  border: 1px solid #f0f0f0;
  
  &:active {
    background: #f9f9f9;
  }
}

.time-section {
  min-width: 4rem;
}

.show-time {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
}

.end-time {
  font-size: 0.75rem;
  color: #999;
  margin-top: 0.15rem;
}

.hall-section {
  flex: 1;
  padding: 0 0.6rem;
  min-width: 0;
}

.version-type {
  font-size: 0.95rem;
  font-weight: 500;
  color: #333;
}

.hall-name {
  font-size: 0.8rem;
  color: #999;
  margin-top: 0.15rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.price-section {
  text-align: right;
  min-width: 4.5rem;
}

.price-row {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
}

.price-value {
  font-size: 1.1rem;
  font-weight: 600;
  color: #ee0a24;
}

.price-unit {
  font-size: 0.75rem;
  color: #ee0a24;
}

.price-suffix {
  font-size: 0.7rem;
  color: #999;
}

.buy-btn {
  display: inline-block;
  margin-top: 0.2rem;
  padding: 0.2rem 0.7rem;
  border: 1px solid #ee0a24;
  border-radius: 1rem;
  color: #ee0a24;
  font-size: 0.7rem;
  font-weight: 500;
}

/* 底部提示 */
.finished-text {
  text-align: center;
  padding: 0.8rem;
  color: #969799;
  font-size: 0.8rem;
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  
  .empty-icon {
    font-size: 2.5rem;
    margin-bottom: 0.8rem;
    opacity: 0.5;
  }
  
  .empty-text {
    color: #999;
    font-size: 0.9rem;
  }
}

/* 轮播样式覆盖 */
::v-deep .carousel-3d-slide {
  position: absolute;
  opacity: 0;
  visibility: hidden;
  overflow: hidden;
  top: 0;
  border-color: transparent;
  border-style: solid;
  background-size: cover;
  background-color: transparent;
  display: block;
  margin: 0;
  box-sizing: border-box;
  text-align: left;
}
</style>